import { useEffect, useState } from "react"

import {
  Input as ChakraInput,
  InputAddonProps as ChakraInputAddonProps,
  InputElementProps as ChakraInputElementProps,
  InputGroup as ChakraInputGroup,
  InputLeftAddon as ChakraInputLeftAddon,
  InputLeftElement as ChakraInputLeftElement,
  InputProps as ChakraInputProps,
  InputRightAddon as ChakraInputRightAddon,
  InputRightElement as ChakraInputRightElement,
  forwardRef,
} from "@chakra-ui/react"

import { DateInput } from "./DateInput"
import { FormControl, SharedFormControlProps } from "./FormControl"

export interface InputProps extends ChakraInputProps, SharedFormControlProps {
  /**
   * Text added to left side of input
   */
  inputLeftAddon?: ChakraInputAddonProps
  /**
   * Add an icon, button or other element to the left side of the input
   */
  inputLeftElement?: ChakraInputElementProps
  /**
   * Text added to right side of input
   */
  inputRightAddon?: ChakraInputAddonProps
  /**
   * Add an icon, button or other element to the right side of the input
   */
  inputRightElement?: ChakraInputElementProps
}

export const Input = forwardRef<InputProps, "input">(
  (
    {
      errorText,
      helperText,
      inputLeftAddon,
      inputLeftElement,
      inputRightAddon,
      inputRightElement,
      isDisabled,
      isInvalid,
      isReadOnly,
      isRequired,
      label,
      size,
      type = "text",
      ...rest
    },
    ref,
  ) => {
    const [isDateInputDisabled, setIsDateInputDisabled] =
      useState<boolean>(false)
    const isDateInput = type.match(/^(date|month|year)$/)

    useEffect(() => {
      if (isDateInput && rest.name?.includes("to_date") && !rest.value) {
        setIsDateInputDisabled(true)
      }
      // eslint-disable-next-line
    }, [])

    const FormControlValues = {
      errorText,
      helperText: helperText,
      isDisabled: isDateInputDisabled || isDisabled,
      isInvalid: !isDateInputDisabled && isInvalid,
      isReadOnly,
      isRequired,
      label,
      isDateInput,
    }

    const getInputLeftComponent = () => {
      if (inputLeftAddon) {
        return <ChakraInputLeftAddon {...inputLeftAddon} />
      }
      if (inputLeftElement) {
        return <ChakraInputLeftElement {...inputLeftElement} />
      }
      return null
    }

    const getInputRightComponent = () => {
      if (inputRightAddon) {
        return <ChakraInputRightAddon {...inputRightAddon} />
      }
      if (inputRightElement) {
        return <ChakraInputRightElement {...inputRightElement} />
      }
      return null
    }

    const InputLeftAddon = () => getInputLeftComponent()
    const InputRightAddon = () => getInputRightComponent()

    return (
      <FormControl {...(FormControlValues as SharedFormControlProps)}>
        <ChakraInputGroup size={size}>
          <InputLeftAddon />
          {isDateInput ? (
            <DateInput
              type={type}
              {...{
                setIsDateInputDisabled,
                isDateInputDisabled,
                errorText,
                ...rest,
              }}
            />
          ) : (
            <ChakraInput type={type} ref={ref} {...rest} />
          )}
          <InputRightAddon />
        </ChakraInputGroup>
      </FormControl>
    )
  },
)
